<script setup>
import { computed, ref, watch } from 'vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';
import SidebarGroupSeparator from './SidebarGroupSeparator.vue';
import Icon from 'next/icon/Icon.vue';

import { useSidebarContext } from './provider';
import { useEventListener } from '@vueuse/core';

const props = defineProps({
  isExpanded: { type: Boolean, default: false },
  label: { type: String, required: true },
  icon: { type: [Object, String], required: true },
  children: { type: Array, default: undefined },
  activeChild: { type: Object, default: undefined },
  collapsible: { type: Boolean, default: false },
});

const isLocalExpanded = ref(false);

const hasActiveChild = computed(() =>
  props.children?.some(child => child.name === props.activeChild?.name)
);

watch(
  () => props.activeChild,
  val => {
    if (val && props.collapsible && hasActiveChild.value) {
      isLocalExpanded.value = true;
    }
  },
  { immediate: true }
);

const effectiveExpanded = computed(() =>
  props.collapsible ? isLocalExpanded.value : props.isExpanded
);

const toggleExpanded = () => {
  isLocalExpanded.value = !isLocalExpanded.value;
};

const { isAllowed } = useSidebarContext();
const scrollableContainer = ref(null);

const accessibleItems = computed(() =>
  props.children.filter(child => {
    if (child.children) {
      return child.children.some(sub => sub.to && isAllowed(sub.to));
    }
    return child.to && isAllowed(child.to);
  })
);

const hasAccessibleItems = computed(() => {
  return accessibleItems.value.length > 0;
});

const isScrollable = computed(() => {
  return accessibleItems.value.length > 7;
});

const scrollEnd = ref(false);

// set scrollEnd to true when the scroll reaches the end
useEventListener(scrollableContainer, 'scroll', () => {
  const { scrollHeight, scrollTop, clientHeight } = scrollableContainer.value;
  scrollEnd.value = scrollHeight - scrollTop === clientHeight;
});
</script>

<template>
  <template v-if="hasAccessibleItems">
    <div
      v-if="collapsible"
      v-show="isExpanded || hasActiveChild"
      class="flex items-center gap-2 px-2 py-1.5 rounded-lg h-8 text-n-slate-10 select-none cursor-pointer hover:bg-n-alpha-2 my-1"
      @click="toggleExpanded"
    >
      <Icon v-if="icon" :icon="icon" class="size-4" />
      <span class="text-sm font-medium leading-5 flex-grow">{{ label }}</span>
      <span
        class="i-lucide-chevron-right size-3 transition-transform duration-200"
        :class="{ 'rotate-90': isLocalExpanded }"
      />
    </div>
    <SidebarGroupSeparator
      v-else
      v-show="isExpanded"
      :label
      :icon
      class="my-1"
    />
  </template>
  <ul v-if="children.length" class="m-0 list-none reset-base relative group">
    <!-- Each element has h-8, which is 32px, we will show 7 items with one hidden at the end,
    which is 14rem. Then we add 16px so that we have some text visible from the next item  -->
    <div
      ref="scrollableContainer"
      :class="{
        'max-h-[calc(14rem+16px)] overflow-y-scroll no-scrollbar': isScrollable,
      }"
    >
      <template v-for="child in children" :key="child.name">
        <template v-if="child.children">
          <SidebarGroupSeparator
            v-show="effectiveExpanded"
            :label="child.label"
            class="my-1"
          />
          <SidebarGroupLeaf
            v-for="subChild in child.children"
            :key="subChild.name"
            v-show="effectiveExpanded || activeChild?.name === subChild.name"
            v-bind="subChild"
            :active="activeChild?.name === subChild.name"
          />
        </template>
        <SidebarGroupLeaf
          v-else
          v-show="effectiveExpanded || activeChild?.name === child.name"
          v-bind="child"
          :active="activeChild?.name === child.name"
        />
      </template>
    </div>
    <div
      v-if="isScrollable && effectiveExpanded"
      v-show="!scrollEnd"
      class="absolute bg-gradient-to-t from-n-solid-2 w-full h-12 to-transparent -bottom-1 pointer-events-none flex items-end justify-end px-2 animate-fade-in-up"
    >
      <svg
        width="16"
        height="24"
        viewBox="0 0 16 24"
        fill="none"
        class="text-n-slate-9 opacity-50 group-hover:opacity-100"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4 4L8 8L12 4"
          stroke="currentColor"
          opacity="0.5"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M4 10L8 14L12 10"
          stroke="currentColor"
          opacity="0.75"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M4 16L8 20L12 16"
          stroke="currentColor"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </ul>
</template>
